import {IntlShape} from 'react-intl';
import {encodeGlobalId} from '../../forecast-app/shared/util/GlobalIdUtil';
import Util from '../../forecast-app/shared/util/util';
import {
	BaselineItem,
	BaselineItemsProject,
	BaselineTotals,
	DeliverableEstimateRef,
	Deliverable,
	ExpenseRow,
	Phase,
	PhaseBaselineExpenseEdges,
	PhaseBaselineRoleEdges,
	Project,
	ProjectPhaseDeliverable,
	RoleRow,
} from './BaselineTypes';

export const PHASE_COLUMNS = {
	PHASE_NAME: 'phase_name',
	PHASE_START_DATE: 'phase_start_date',
	PHASE_DEADLINE_DATE: 'phase_deadline_date',
	ITEM_TYPE: 'item_type',
	ROLE: 'role',
	EXPENSE_NAME: 'expense_name',
	EXPENSE_CATEGORY: 'expense_category',
	DELIVERABLE_NAME: 'deliverable_name',
	HOURS: 'hours',
	VALUE_OF_SERVICE: 'value_of_service',
	REVENUE: 'revenue',
	COST: 'cost',
	MARKUP: 'markup',
	PROFIT: 'profit',
	MARGIN: 'margin',
	RATE_PER_HOUR: 'rate_per_hour',
	COST_PER_HOUR: 'cost_per_hour',
};

export const EXPORT_COLUMNS = [
	PHASE_COLUMNS.PHASE_NAME,
	PHASE_COLUMNS.PHASE_START_DATE,
	PHASE_COLUMNS.PHASE_DEADLINE_DATE,
	PHASE_COLUMNS.ITEM_TYPE,
	PHASE_COLUMNS.ROLE,
	PHASE_COLUMNS.HOURS,
	PHASE_COLUMNS.EXPENSE_NAME,
	PHASE_COLUMNS.EXPENSE_CATEGORY,
];

const TIME_ESTIMATES_COLUMNS = [
	PHASE_COLUMNS.VALUE_OF_SERVICE,
	PHASE_COLUMNS.REVENUE,
	PHASE_COLUMNS.COST,
	PHASE_COLUMNS.PROFIT,
	PHASE_COLUMNS.RATE_PER_HOUR,
	PHASE_COLUMNS.COST_PER_HOUR,
];
const EXPENSE_ESTIMATES_COLUMNS = [
	PHASE_COLUMNS.REVENUE,
	PHASE_COLUMNS.COST,
	PHASE_COLUMNS.MARKUP,
	PHASE_COLUMNS.PROFIT,
	PHASE_COLUMNS.MARGIN,
];

const TIME_ESTIMATES = 'time_estimates';
const EXPENSE_ESTIMATES = 'expense_estimates';

const EYE_OPTION_COLUMNS = [
	{id: TIME_ESTIMATES, nestedOptions: TIME_ESTIMATES_COLUMNS},
	{id: EXPENSE_ESTIMATES, nestedOptions: EXPENSE_ESTIMATES_COLUMNS},
];

export interface OnSaveError {
	type: OnSaveErrorType;
	message: string;
	phaseId: string;
}

export enum OnSaveErrorType {
	MISSING_ROLE = 'MISSING_ROLE',
}

/**
 * The line's Deliverable as a global id that matches the Deliverable node ids on the same page. The
 * server's `globalDeliverableId` is used when fetched, since a Baseline version's Deliverables carry
 * their own type name; otherwise the live Deliverable id is minted from `deliverableId`.
 */
function getGlobalDeliverableId(
	item: {node?: {deliverableId?: number | null; globalDeliverableId?: string | null} | null} | null
): string {
	if (item?.node?.globalDeliverableId) {
		return item.node.globalDeliverableId;
	}
	return item?.node?.deliverableId ? encodeGlobalId('DeliverableType', Number(item.node.deliverableId)) : '';
}

/** Columns that show cost, or give it away: margin and markup are revenue measured against cost. */
export const COST_COLUMNS = [
	PHASE_COLUMNS.COST,
	PHASE_COLUMNS.PROFIT,
	PHASE_COLUMNS.MARGIN,
	PHASE_COLUMNS.MARKUP,
	PHASE_COLUMNS.COST_PER_HOUR,
];

export const withoutCostColumns = (columns: string[]): string[] => columns.filter(column => !COST_COLUMNS.includes(column));

/** The eye's options. A viewer who cannot see cost is not offered the cost columns. */
export const phaseEyeOptions = (seesCost: boolean) =>
	EYE_OPTION_COLUMNS.map(column => ({
		name: column.id,
		translationId: 'phase_management.column.' + column.id,
		disabled: false,
		checked: true,
		nestedOptions: (seesCost ? column.nestedOptions : withoutCostColumns(column.nestedOptions)).map(nestedColumn => ({
			name: nestedColumn,
			translationId: 'phase_management.column.' + nestedColumn,
			disabled: false,
			checked: true,
		})),
	}));

export const PHASE_EYE_OPTIONS = phaseEyeOptions(true);

type RoleFinancialNumbersNode = NonNullable<
	NonNullable<NonNullable<PhaseBaselineRoleEdges[number]>['node']>['financialNumbers']
>;
type ExpenseNode = NonNullable<NonNullable<PhaseBaselineExpenseEdges[number]>['node']>;

/**
 * A role's money as graphql returns it. The Quote Designer does not ask for cost, so every field is optional
 * here. The keys are picked from the full Baseline type, so a schema rename still fails the type check.
 */
type RoleFinancialNumbers = Partial<
	Pick<
		RoleFinancialNumbersNode,
		| 'baselineRevenue'
		| 'baselineCost'
		| 'baselineProfit'
		| 'baselineTimeAndExpenses'
		| 'baselineCostPerHour'
		| 'baselineRatePerHour'
	>
>;

/**
 * An expense's money as graphql returns it. The Quote Designer does not ask for cost, markup or profit. Revenue is
 * kept in the pick because it is the field every query shares.
 */
type ExpenseMoney = Partial<Pick<ExpenseNode, 'expenseRevenue' | 'expenseCost' | 'expenseMarkup' | 'expenseProfit'>>;

export const getInitialBaselineItems = (project: BaselineItemsProject | null, useDeliverables: boolean): BaselineItem[] => [
	...(project?.phaseBaselineRoles?.edges
		?.filter(role => role?.node?.role && role?.node?.financialNumbers)
		.map(role => {
			const {
				baselineRevenue,
				baselineCost,
				baselineProfit,
				baselineTimeAndExpenses,
				baselineCostPerHour,
				baselineRatePerHour,
			}: RoleFinancialNumbers = role?.node?.financialNumbers || {};
			const baselineMinutes = role?.node?.baselineMinutes || 0;
			const revenueRaw = baselineRevenue || 0;
			const costRaw = baselineCost || 0;
			const hoursRaw = (baselineMinutes || 0) / 60;
			const profitRaw = baselineProfit || 0;
			const valueOfServiceRaw = baselineTimeAndExpenses || 0;

			const [hours, valueOfService, revenue, cost, profit, ratePerHour, costPerHour]: number[] = [
				hoursRaw,
				valueOfServiceRaw,
				revenueRaw,
				costRaw,
				profitRaw,
				baselineRatePerHour,
				baselineCostPerHour || 0,
			].map(Util.roundFloatToTwoDecimals);

			const globalDeliverableId = useDeliverables ? getGlobalDeliverableId(role) : undefined;

			return {
				phaseBaselineRoleId: role?.node?.id || '',
				projectId: project.id || '',
				phaseId: role?.node?.globalPhaseId || '',
				deliverableId: globalDeliverableId,
				roleId: role?.node?.role?.id || '',
				roleName: role?.node?.role?.name || '',
				hours,
				valueOfService,
				revenue,
				cost,
				profit,
				ratePerHour,
				costPerHour,
			};
		}) || []),
	...(project?.phaseBaselineExpenses?.edges
		?.filter(expense => expense?.node)
		.map(expense => {
			const revenueRaw = expense?.node?.expenseRevenue || 0;
			const costFields: ExpenseMoney = expense?.node ?? {};
			const costRaw = costFields.expenseCost || 0;

			const [roundedRevenue, roundedCost, roundedMarkup, roundedProfit, roundedMargin]: number[] = [
				revenueRaw,
				costRaw,
				costFields.expenseMarkup || 0,
				costFields.expenseProfit || 0,
				revenueRaw ? (revenueRaw - costRaw) / revenueRaw : 0,
			].map(Util.roundFloatToTwoDecimals);

			const name = expense?.node?.expenseName ?? '';
			const categoryName = expense?.node?.expenseCategory?.name ?? '';
			const categoryId = expense?.node?.expenseCategory?.id ?? '';

			const globalDeliverableId = useDeliverables ? getGlobalDeliverableId(expense) : undefined;

			return {
				phaseBaselineExpenseId: expense?.node?.id || '',
				projectId: project.id || '',
				phaseId: expense?.node?.globalPhaseId || '',
				deliverableId: globalDeliverableId,
				categoryName,
				categoryId,
				revenue: roundedRevenue,
				cost: roundedCost,
				markup: roundedMarkup,
				profit: roundedProfit,
				margin: roundedMargin,
				name,
			};
		}) || []),
];

type EstimateRowEdges<Node> = ReadonlyArray<{readonly node?: Node | null} | null> | null;

type DeliverableEstimateRowsProject = {
	readonly phaseBaselineRoles?: {
		readonly edges?: EstimateRowEdges<{
			readonly deliverableId?: number | null;
			readonly globalDeliverableId?: string | null;
			readonly role?: {readonly id: string} | null;
		}>;
	} | null;
	readonly phaseBaselineExpenses?: {
		readonly edges?: EstimateRowEdges<{
			readonly deliverableId?: number | null;
			readonly globalDeliverableId?: string | null;
		}>;
	} | null;
};

/**
 * Which Deliverables have Baseline estimates, as refs for the Deliverable modals' delete warning.
 * `undefined` while neither connection has loaded, so the warning says it can't confirm rather than
 * claiming there are none. Role rows need a role, as in `getInitialBaselineItems`; its financial-numbers
 * check is skipped because callers of this don't fetch them, which can only err towards warning.
 */
export const getDeliverableEstimateRefs = (
	project: DeliverableEstimateRowsProject | null | undefined
): DeliverableEstimateRef[] | undefined => {
	if (project?.phaseBaselineRoles === undefined && project?.phaseBaselineExpenses === undefined) {
		return undefined;
	}
	const roleRows = project?.phaseBaselineRoles?.edges?.filter(edge => edge?.node?.role) ?? [];
	const expenseRows = project?.phaseBaselineExpenses?.edges ?? [];
	return [...roleRows, ...expenseRows]
		.map(row => getGlobalDeliverableId(row))
		.filter(deliverableId => deliverableId !== '')
		.map(deliverableId => ({deliverableId}));
};

export const getInitialPhaseDeliverables = (project: Project | null): ProjectPhaseDeliverable[] =>
	project?.phases?.edges
		?.map(phaseEdge =>
			phaseEdge?.node?.phaseDeliverables?.edges
				?.filter(phaseDeliverableEdge => phaseDeliverableEdge?.node && phaseDeliverableEdge?.node.deliverable)
				.map(phaseDeliverableEdge => ({
					id: phaseDeliverableEdge?.node?.id,
					phaseId: phaseDeliverableEdge?.node?.phaseId,
					deliverable: phaseDeliverableEdge?.node?.deliverable,
					financialNumbers: phaseDeliverableEdge?.node?.financialNumbers,
				}))
		)
		.flat(2)
		.filter((item): item is ProjectPhaseDeliverable => !!item) || [];

export const createNodeIdLookup = <T extends {id: string}>(nodes: (T | null | undefined)[]): Map<string, T> =>
	new Map<string, T>(nodes.filter(Boolean).map((d: T): [string, T] => [d.id, d]));

export function formatAndExportCSV(
	columnNames: string[],
	filename: string,
	initialBaselineItems: BaselineItem[],
	phaseLookup: Map<string, Phase>,
	deliverableLookup: Map<string, Deliverable>,
	intl: IntlShape
): void {
	const fullFileName = filename + '.csv';

	function getDeliverableName(item: BaselineItem) {
		return item.deliverableId && deliverableLookup.get(item.deliverableId)?.name;
	}

	function formatPhaseDate(year?: number | null, month?: number | null, day?: number | null): string {
		return year && month && day ? Util.CreateNonUtcMomentDate(year, month, day).format('YYYY-MM-DD') : '';
	}

	const mappedRows = initialBaselineItems.map((item: BaselineItem) => {
		const phase = phaseLookup.get(item.phaseId) as Phase & {
			baselineStartYear?: number | null;
			baselineStartMonth?: number | null;
			baselineStartDay?: number | null;
			baselineDeadlineYear?: number | null;
			baselineDeadlineMonth?: number | null;
			baselineDeadlineDay?: number | null;
		};
		const isWorkItem = 'phaseBaselineRoleId' in item;
		const mappings = {
			[PHASE_COLUMNS.PHASE_NAME]: phase?.name,
			[PHASE_COLUMNS.PHASE_START_DATE]: formatPhaseDate(
				phase?.baselineStartYear,
				phase?.baselineStartMonth,
				phase?.baselineStartDay
			),
			[PHASE_COLUMNS.PHASE_DEADLINE_DATE]: formatPhaseDate(
				phase?.baselineDeadlineYear,
				phase?.baselineDeadlineMonth,
				phase?.baselineDeadlineDay
			),
			[PHASE_COLUMNS.ITEM_TYPE]: isWorkItem
				? intl.formatMessage({id: 'baseline.work'})
				: intl.formatMessage({id: 'common.expense'}),
			[PHASE_COLUMNS.DELIVERABLE_NAME]: getDeliverableName(item),
			[PHASE_COLUMNS.ROLE]: item.roleName,
			[PHASE_COLUMNS.HOURS]: item.hours,
			[PHASE_COLUMNS.VALUE_OF_SERVICE]: item.valueOfService,
			[PHASE_COLUMNS.REVENUE]: item.revenue,
			[PHASE_COLUMNS.COST]: item.cost,
			[PHASE_COLUMNS.PROFIT]: item.profit,
			[PHASE_COLUMNS.RATE_PER_HOUR]: item.ratePerHour,
			[PHASE_COLUMNS.COST_PER_HOUR]: item.costPerHour,
			[PHASE_COLUMNS.EXPENSE_NAME]: item.name,
			[PHASE_COLUMNS.EXPENSE_CATEGORY]: item.categoryName,
			[PHASE_COLUMNS.MARKUP]: item.markup,
			[PHASE_COLUMNS.MARGIN]: item.margin,
		};
		return columnNames.map(header => {
			const value = `${mappings[header] ?? ''}`;
			return value.includes(',') ? `"${value}"` : value;
		});
	});

	const exportData = [columnNames, ...mappedRows].join('\r\n');

	Util.exportToCSV(exportData, fullFileName);
}

export const getTotals = (baselineItems: BaselineItem[]): BaselineTotals => {
	const totals = baselineItems.reduce(
		(result, currentItem) => {
			if ('phaseBaselineRoleId' in currentItem) {
				const item = currentItem as RoleRow;
				result.baselineMinutes += item.hours * 60;
				result.baselineCost += item.cost;
				result.baselineRevenue += item.revenue;
				result.baselineTimeAndExpenses += item.valueOfService;
			} else if ('phaseBaselineExpenseId' in currentItem) {
				const item = currentItem as ExpenseRow;
				result.baselineCost += item.cost;
				result.baselineRevenue += item.revenue;
				result.baselineTimeAndExpenses += Math.max(item.revenue, item.cost);
			}
			return result;
		},
		{
			baselineCost: 0,
			baselineMargin: 0,
			baselineMinutes: 0,
			baselineProfit: 0,
			baselineRevenue: 0,
			baselineTimeAndExpenses: 0,
		}
	);

	totals.baselineProfit = totals.baselineRevenue - totals.baselineCost;
	totals.baselineMargin = totals.baselineRevenue
		? Util.roundFloatToTwoDecimals(totals.baselineProfit / totals.baselineRevenue)
		: 0;
	return totals;
};

/**
 * Baseline estimates are stored in minutes. When a project sets a
 * "Time Estimation Unit" of Days (project.baselineEstimationMinutesPerDay),
 * the Baseline page displays and accepts days instead — a boundary concern
 * only. These helpers are that boundary.
 */
export const getMinutesPerDay = (project: Project): number | null => project?.baselineEstimationMinutesPerDay ?? null;

export const estimateIsInDays = (minutesPerDay: number | null | undefined): minutesPerDay is number =>
	!!minutesPerDay && minutesPerDay > 0;

export const hoursToDays = (hours: number, minutesPerDay: number): number =>
	Math.round(((hours * 60) / minutesPerDay) * 100) / 100;

export const daysToHours = (days: number, minutesPerDay: number): number => Math.round(days * minutesPerDay) / 60;

export const formatBaselineEstimateTotal = (
	baselineMinutes: number,
	minutesPerDay: number | null,
	intl: IntlShape
): {titleId: string; value: string} =>
	estimateIsInDays(minutesPerDay)
		? {
				titleId: 'common.total_days',
				value: Util.convertMinutesToDays(baselineMinutes || 0, minutesPerDay, intl),
		  }
		: {
				titleId: 'common.total_hours',
				value: Util.convertMinutesToFullHour(baselineMinutes || 0, intl),
		  };
